'use client'

/**
 * Professional sign-out surface — ui-ux-pro-max
 *
 * Minimal single-column · banking trust · IBM Plex · cream + rose gold.
 * Shown only while session teardown runs (never for app boot).
 * Subtle motion (150–300ms language), reduced-motion safe, AA contrast.
 */

import React from 'react'
import { clientLoginHref, clearSigningOutFlag } from '@/domains/auth/utils/sign-out'
import styles from './feedback.module.css'

export interface SignOutScreenProps {
  /** Primary line — keep short and calm */
  title?: string
  /** Supporting trust copy */
  description?: string
  className?: string
  'aria-label'?: string
}

export function SignOutScreen({
  title = 'Signed out',
  description = 'Session ended. Returning to sign in.',
  className = '',
  'aria-label': ariaLabel,
}: SignOutScreenProps) {
  const label = ariaLabel || title

  return (
    <div
      className={`${styles.signOut} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      data-auth-phase="signout"
    >
      {/* Soft ambient field — no clutter, no nav */}
      <div className={styles.signOutAmbient} aria-hidden="true" />

      <div className={styles.signOutPanel}>
        <div className={styles.signOutBrand}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/images/RobotBrand.jpg"
            alt=""
            width={40}
            height={40}
            className={styles.signOutMark}
          />
          <div className={styles.signOutBrandCopy}>
            <span className={styles.signOutBrandName}>Mechagora</span>
            <span className={styles.signOutBrandMeta}>Trading</span>
          </div>
        </div>

        <div className={styles.signOutBody}>
          <h1 className={styles.signOutTitle}>{title}</h1>
          <p className={styles.signOutDesc}>{description}</p>
        </div>

        {/* Single status cue — sweep bar only (no spinner + bar stack) */}
        <div
          className={styles.signOutProgress}
          role="progressbar"
          aria-label={label}
          aria-valuetext="Signing out"
          aria-busy="true"
        >
          <span className={styles.signOutProgressBar} />
        </div>

        <p className={styles.signOutFoot}>Secure session end</p>
        <button
          type="button"
          className={styles.signOutContinue}
          onClick={() => {
            clearSigningOutFlag()
            window.location.replace(
              clientLoginHref({ error: 'session_expired' })
            )
          }}
        >
          Continue to sign in
        </button>
      </div>
    </div>
  )
}

export default SignOutScreen
